<template>
    <div>
        <Hero colour-index="0" :title="`${INTEGRATIONS.length} Integrations & MCP Servers`"
            subtitle="Every API integration & MCP server listed is live and ready to use."
            :buttons="[{ link: 'https://app.unified.to', title: 'Get started' }]" />

        <div class="w-full my-12 px-3 sm:px-28">
            <form>
                <input ref="searchInput" type="text" id="voice-search"
                    class="search-bar text-md rounded-lg block w-full py-4 px-3" placeholder="Search..." required
                    v-model.trim="search" />
            </form>
        </div>

        <div class="flex my-12 px-3 sm:px-28">
            <div class="lg:w-1/4 hidden lg:inline-block">
                <div class="rounded mr-12 border shadow leading-loose ">
                    <div v-for="cat in categories" class="border-b-2 border-gray-800">
                        <button href="#" @click="selectedCategory = cat.value"
                            :class="selectedCategory === cat.value ? 'bg-[var(--hover-gray)] text-white' : 'text-medium-gray'"
                            class="block w-full  text-left py-2 px-4 hover:bg-[var(--hover-gray)] hover:text-[var(--light-gray)]">
                            {{ cat.label }}

                            <Badge class="float-right mt-1" v-if="category_count[cat.value as TIntegrationCategory]"
                                :label="category_count[cat.value as TIntegrationCategory].toString()" color="gray" />

                        </button>
                    </div>
                </div>
                <div class="mr-12 mt-8">
                    <NuxtLink to="/integrations/roadmap" class="btn text-sm w-full text-center btn-primary">
                        See what's on the roadmap
                    </NuxtLink>
                </div>
            </div>
            <div
                class="lg:w-3/4 justify-top grid gap-4 w-full sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 content-start">
                <Integration :integration="i" v-for="i in filteredIntegrations" :key="i.type" />
                <div v-if="search && !filteredIntegrations.length"
                    class="col-span-full flex flex-col items-center justify-center py-16 text-center">
                    <p class="text-medium-gray mb-6">No integrations match “{{ search }}”.</p>
                    <NuxtLink to="/integrations/roadmap" class="btn text-sm text-center btn-primary">
                        Check the roadmap
                    </NuxtLink>
                </div>
            </div>
        </div>

        <Hero title="Unleash your full integration potential" heading-level="h2" colour-index="3" :buttons="[
            { link: 'https://app.unified.to', title: 'Get started' },
            { link: '/contact', title: 'Talk to our team', icon: 'ic:round-keyboard-arrow-right' },
        ]" subtitle="Sign up in seconds and start building integrations today." />
    </div>
</template>

<script setup lang="ts">
import { setTitle } from '~~/services/common';

setTitle(
    'All Integrations',
    'Stop waiting months or years to ship new integrations. Create an account in seconds. Launch secure and deep API-first integrations in hours. Build for HR, sales, marketing, support, ticketing, and more.'
);

// /integrations?category=<x> is a client-side filter of the same hub page, so every
// variant canonicalises to the bare hub URL (TICKET-10398).
useHead({
    link: [{ rel: 'canonical', href: 'https://unified.to/integrations' }],
});
</script>

<script lang="ts">
import { CATS, INTEGRATIONS } from '~/services/integrations';
import { MAP_CATS } from '~~/services/categories';
import HomeSection from '~/components/HomeSection.vue';
import { type TIntegrationCategory } from '~/models/Unified';

interface ICategory {
    label: string;
    value: TIntegrationCategory | string;
}

export default {
    components: { HomeSection },

    data() {
        const categories = CATS.map((c) => {
            return {
                label: MAP_CATS[c as keyof typeof MAP_CATS],
                value: c === 'scim' ? 'hris' : c as string,
            } satisfies ICategory
        });

        categories.sort((a, b) => a.label.localeCompare(b.label));

        return {
            search: '',
            INTEGRATIONS,
            categories: [
                { label: 'All', value: '' } satisfies ICategory,
                { label: 'Recently Added', value: 'new' } satisfies ICategory,
            ].concat(categories),

            // .concat([{ label: 'In Beta / Coming Soon', value: 'soon' }])

            selectedCategory: '',
            category_count: {} as Record<TIntegrationCategory | 'new', number>,
        };
    },
    computed: {
        // Combine search and category filters into one
        filteredIntegrations() {
            const search = this.search?.toLowerCase() || '';
            return this.INTEGRATIONS.filter((i) => {
                const matchesSearch = !search || i.type.toLowerCase().includes(search) || i.name.toLowerCase().includes(search);
                const matchesCategory = !this.selectedCategory ||
                    i.categories.includes(this.selectedCategory as TIntegrationCategory) ||
                    (this.selectedCategory === 'new' && i.recent);
                return matchesSearch && matchesCategory;
            });
        }
    },
    mounted() {
        const query = this.$route.query.q;
        if (query) {
            this.search = query as string;
        }
        const category = this.$route.query.category;
        if (category) {
            this.selectedCategory = category as string;
        }

        this.category_count.new = this.INTEGRATIONS.filter((i) => i.recent).length;
        for (const cat of CATS) {
            this.category_count[cat as TIntegrationCategory] = this.INTEGRATIONS.filter((i) => i.categories.includes(cat as TIntegrationCategory)).length;
        }
    }
};
</script>